/**
 * CSV and (very lightweight) PDF-ish HTML export. The HTML export is designed
 * to be printed to PDF from the browser; this avoids bundling a heavy PDF lib
 * in the GitHub Pages build.
 */

import type { CampaignKPIs } from "./kpis";

export function toCsv(rows: Record<string, string | number>[]): string {
  if (rows.length === 0) return "";
  const headers = Array.from(
    rows.reduce<Set<string>>((acc, r) => {
      Object.keys(r).forEach((k) => acc.add(k));
      return acc;
    }, new Set()),
  );
  const escape = (v: string | number | undefined) => {
    if (v === undefined || v === null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
  };
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => escape(row[h])).join(","));
  }
  return lines.join("\n");
}

export function kpisToCsv(k: CampaignKPIs): string {
  const rows: Record<string, string | number>[] = [
    { metric: "queued",        value: k.totals.queued },
    { metric: "sent",          value: k.totals.sent },
    { metric: "delivered",     value: k.totals.delivered },
    { metric: "failed",        value: k.totals.failed },
    { metric: "undelivered",   value: k.totals.undelivered },
    { metric: "inbound",       value: k.totals.inbound },
    { metric: "opt_outs",      value: k.totals.optOuts },
    { metric: "delivery_rate", value: k.rates.deliveryRate.toFixed(4) },
    { metric: "response_rate", value: k.rates.responseRate.toFixed(4) },
    { metric: "opt_out_rate",  value: k.rates.optOutRate.toFixed(4) },
    { metric: "completion_rate", value: k.rates.completionRate.toFixed(4) },
  ];
  return toCsv(rows);
}

export interface ReportContext {
  clientName: string;
  campaignName: string;
  generatedAt: string;
  disclosureVersion: string;
  methodology: string;
}

export function kpisToPrintableHtml(k: CampaignKPIs, ctx: ReportContext): string {
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  const answerRows = Object.entries(k.answerDistribution)
    .map(([key, vals]) => {
      const inner = Object.entries(vals)
        .map(([v, c]) => `<tr><td>${v}</td><td style="text-align:right">${c}</td></tr>`)
        .join("");
      return `<h3>${key}</h3><table>${inner}</table>`;
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${escapeHtml(ctx.campaignName)} — Report</title>
<style>
body{font-family:ui-sans-serif,system-ui,sans-serif;max-width:780px;margin:2rem auto;padding:0 1rem;color:#0f172a}
h1{font-size:1.5rem;margin-bottom:0}
.muted{color:#64748b;font-size:.9rem}
table{width:100%;border-collapse:collapse;margin:1rem 0}
td,th{padding:.4rem .6rem;border-bottom:1px solid #e2e8f0;text-align:left}
.kpi-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.5rem}
.kpi{padding:.75rem;border:1px solid #e2e8f0;border-radius:8px}
.kpi .v{font-size:1.25rem;font-weight:600}
.appendix{font-size:.8rem;color:#475569;margin-top:2rem}
@media print{body{margin:0}}
</style></head><body>
<h1>${escapeHtml(ctx.campaignName)}</h1>
<div class="muted">Client: ${escapeHtml(ctx.clientName)} · Generated ${escapeHtml(ctx.generatedAt)}</div>

<h2>Totals</h2>
<div class="kpi-grid">
  <div class="kpi"><div class="muted">Sent</div><div class="v">${k.totals.sent}</div></div>
  <div class="kpi"><div class="muted">Delivered</div><div class="v">${k.totals.delivered}</div></div>
  <div class="kpi"><div class="muted">Failed</div><div class="v">${k.totals.failed}</div></div>
  <div class="kpi"><div class="muted">Inbound</div><div class="v">${k.totals.inbound}</div></div>
  <div class="kpi"><div class="muted">Opt-outs</div><div class="v">${k.totals.optOuts}</div></div>
  <div class="kpi"><div class="muted">Delivery rate</div><div class="v">${pct(k.rates.deliveryRate)}</div></div>
  <div class="kpi"><div class="muted">Response rate</div><div class="v">${pct(k.rates.responseRate)}</div></div>
  <div class="kpi"><div class="muted">Opt-out rate</div><div class="v">${pct(k.rates.optOutRate)}</div></div>
</div>

<h2>By Language</h2>
<table>
<tr><th>Lang</th><th>Sent</th><th>Delivered</th><th>Inbound</th><th>Opt-outs</th></tr>
<tr><td>English</td><td>${k.byLanguage.en.sent}</td><td>${k.byLanguage.en.delivered}</td><td>${k.byLanguage.en.inbound}</td><td>${k.byLanguage.en.optOuts}</td></tr>
<tr><td>Spanish</td><td>${k.byLanguage.es.sent}</td><td>${k.byLanguage.es.delivered}</td><td>${k.byLanguage.es.inbound}</td><td>${k.byLanguage.es.optOuts}</td></tr>
</table>

<h2>Answer Distribution</h2>
${answerRows || "<p class=\"muted\">No structured responses recorded.</p>"}

<div class="appendix">
<h3>Methodology &amp; Compliance Appendix</h3>
<p>${escapeHtml(ctx.methodology)}</p>
<p>Disclosure version: <code>${escapeHtml(ctx.disclosureVersion)}</code>. All sends are gated by the policy engine in <code>@mensajeando/compliance</code> (consent, suppression, quiet hours, sender eligibility). Opt-outs are logged with timestamp, channel, and matched keyword.</p>
</div>
</body></html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c),
  );
}
